import { Button, type ButtonSize } from "../components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItemIndicator,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "../components/dropdown-menu";
import { Icon } from "../components/icon";
import type { IconName } from "../icons";
import type { ThemePreference } from "./theme";
import { useTheme } from "./theme-provider";

export interface ThemeToggleLabels {
  trigger: string;
  heading: string;
  light: string;
  dark: string;
  system: string;
}

const DEFAULT_LABELS: ThemeToggleLabels = {
  trigger: "Toggle theme",
  heading: "Theme",
  light: "Light",
  dark: "Dark",
  system: "System",
};

const OPTIONS: readonly { value: ThemePreference; icon: IconName }[] = [
  { value: "light", icon: "sun" },
  { value: "dark", icon: "moon" },
  { value: "system", icon: "monitor" },
];

const isPreference = (value: string): value is ThemePreference => OPTIONS.some((o) => o.value === value);

export interface ThemeToggleProps {
  labels?: Partial<ThemeToggleLabels>;
  size?: ButtonSize;
  className?: string;
}

/** Moon/sun trigger (as in the mockups) opening Light / Dark / System. */
export function ThemeToggle({ labels, size = "md", className }: ThemeToggleProps) {
  const { preference, resolved, setPreference } = useTheme();
  const text = { ...DEFAULT_LABELS, ...labels };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size={size} iconOnly aria-label={text.trigger} className={className}>
          <Icon name={resolved === "dark" ? "sun" : "moon"} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel>{text.heading}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={preference}
          onValueChange={(value) => {
            if (isPreference(value)) setPreference(value);
          }}
        >
          {OPTIONS.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              <Icon name={option.icon} className="text-fg-subtle" />
              {text[option.value]}
              <DropdownMenuItemIndicator className="ml-auto">
                <Icon name="check" className="text-primary-ink" />
              </DropdownMenuItemIndicator>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
